import { Component, Input, OnInit } from '@angular/core';
import { SafeUrl } from '@angular/platform-browser';
import { SavedPin } from '@models/teranet';
import { SalePinActions, SalePinState } from '@state/sale_pins';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { combineLatest, Observable } from 'rxjs';
import { Select } from '@ngxs/store';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { map } from 'rxjs/operators';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { PurchaseStates, PurchaseStateType } from '@state/utils/models';
import { UploadService } from 'src/app/shared/services/file/upload.service';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { ICON } from '@core/util/icon';
import {
	MapDialogComponent,
	MapDialogData,
} from './map-dialog/map-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { NavigationService } from 'src/app/shared/services/nav/nav.service';
import { TestHooks } from './map.hooks';

enum LotViewState {
	ALL = 'all',
	PART = 'part',
	FULL = 'full',
	OTHER = 'other',
}

export const LotViewStateFilterOptions = [
	{ label: 'All', value: LotViewState.ALL },
	{ label: 'Part lots', value: LotViewState.PART },
	{ label: 'Full lots', value: LotViewState.FULL },
	{ label: 'Other', value: LotViewState.OTHER },
];

@UntilDestroy()
@Component({
	selector: 'app-adjacent-properties-map',
	templateUrl: './map.component.html',
	standalone: false,
})
export class AdjacentPropertiesMapComponent implements OnInit {
	testHooks = TestHooks;
	public imageSrc: SafeUrl;
	public allLots: SavedPin[];
	public partLots: SavedPin[];
	public fullLots: SavedPin[];
	public otherLots: SavedPin[];
	public lotViewState = LotViewState.PART;
	public lotViewStates = LotViewState;
	public adjacentProperties;
	public selectedPin;
	public mapPurchasedState;
	public purchaseStates = PurchaseStateType;
	public ICON = ICON;

	public selectedPartLots = [];
	public lotViewStateFilterOptions = LotViewStateFilterOptions;
	@Input() showBackButton = true;

	@SelectSnapshot(SalePinState.getPrimary) primary: SavedPin;
	@SelectSnapshot(SelectedMatter.id) saleId: string;
	@Select(SalePinState.getAdjacent) adjacentProperties$: Observable<SavedPin[]>;
	@Select(SalePinState.getSelected) selectedPin$: Observable<SavedPin>;
	@SelectSnapshot(SalePinState.getMapPurchaseState)
	mapPurchaseStates: PurchaseStates;

	constructor(
		private uploadService: UploadService,
		private dialog: MatDialog,
		private routeService: NavigationService,
	) {}

	ngOnInit(): void {
		combineLatest([this.adjacentProperties$, this.selectedPin$])
			.pipe(
				untilDestroyed(this),
				map(async ([adjProperties, selectedPin]) => {
					this.selectedPin = selectedPin;

					this.imageSrc = await this.uploadService.getAbsoluteFileUrl(
						selectedPin?.map?.uploaded_document_path,
					); // this.domSanitizer.bypassSecurityTrustUrl(`data:image/png;base64, ${selectedPin?.map?.uploaded_document_path}`)

					const adjPinsFlattened = selectedPin?.map?.adjacent_parcels
						?.filter((x) => x.type === 'P')
						.map((x) => x.pin);
					if (adjPinsFlattened?.length > 0 && adjProperties?.length > 0) {
						this.partLots = adjProperties?.filter(
							(x) => x.partLot && adjPinsFlattened?.includes(x.pin),
						);
						this.fullLots = adjProperties?.filter(
							(x) => !x.partLot && adjPinsFlattened?.includes(x.pin),
						);
					}

					const otherPinsFlattened = selectedPin?.map?.adjacent_parcels
						?.filter((x) => x.type !== 'P')
						.map((x) => x.pin);
					this.otherLots = adjProperties?.filter((x) =>
						otherPinsFlattened?.includes(x.pin),
					);

					if (this.partLots?.length === 0) {
						this.lotViewState =
							this.fullLots?.length === 0
								? LotViewState.OTHER
								: LotViewState.FULL;
					}

					this.allLots = [
						...(this.partLots ?? []),
						...(this.fullLots ?? []),
						...(this.otherLots ?? []),
					];

					this.lotViewStateFilterOptions = [
						{
							label: `All ${adjProperties?.length ?? 0}`,
							value: LotViewState.ALL,
						},
						{
							label: `Part lots ${this.partLots?.length ?? 0}`,
							value: LotViewState.PART,
						},
						{
							label: `Full lots ${this.fullLots?.length ?? 0}`,
							value: LotViewState.FULL,
						},
						{
							label: `Other ${this.otherLots?.length ?? 0}`,
							value: LotViewState.OTHER,
						},
					];
				}),
			)
			.subscribe();
	}

	@Dispatch()
	_purchaseMap(pin: SavedPin) {
		return new SalePinActions.PurchaseAdjacentMap(pin);
	}

	purchaseMap = () => {
		this._purchaseMap(this.selectedPin);
	};

	expandImage = () => {
		const data: MapDialogData = {
			imageSrc: this.imageSrc,
		};

		this.dialog.open(MapDialogComponent, {
			data,
			maxWidth: '780px',
			width: '90%',
		});
	};

	setLotViewState = (lotViewState: LotViewState) => {
		this.lotViewState = lotViewState;
	};

	goBackToOverview = (): void => {
		this.routeService.goToMatter_TitleSearch_SearchOverview(this.saleId);
	};
}
